/**
 * Phase 20 Plan 20-02 — NotificationSettingsPage
 *
 * Permission gate: requires notifications.manage_settings.
 * Two sub-tabs: Recipients and Subscription Matrix.
 */

import type { JSX } from 'react';

import { RecipientsListTab } from '@/components/notifications/RecipientsListTab';
import { SubscriptionMatrixTab } from '@/components/notifications/SubscriptionMatrixTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth/AuthContext';

export function NotificationSettingsPage(): JSX.Element {
  const { permissions } = useAuth();
  const canManage = permissions.includes('notifications.manage_settings');

  if (!canManage) {
    return (
      <div
        data-testid="permission-denied-panel"
        className="flex min-h-[200px] items-center justify-center rounded border border-destructive/30 bg-destructive/5 p-8 text-center"
      >
        <div>
          <p className="font-medium text-destructive">Insufficient permissions</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You need the <code>notifications.manage_settings</code> permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Notification Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage notification recipients and subscription preferences.
        </p>
      </div>

      <Tabs defaultValue="recipients">
        <TabsList>
          <TabsTrigger value="recipients">Recipients</TabsTrigger>
          <TabsTrigger value="subscription-matrix">Subscription Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="recipients" className="mt-4">
          <RecipientsListTab />
        </TabsContent>

        <TabsContent value="subscription-matrix" className="mt-4">
          <SubscriptionMatrixTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
